import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Center } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { useCreateCalendarMutation } from 'src/meetings/hooks/useCreateCalendarMutation';
import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import { useCalendars } from 'src/meetings/hooks/useCalendars';
import { CalendarPlatform } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';

type Props = {
  platform: CalendarPlatform;
};

const OAUTH_USER_ERRORS = ['access_denied'];

export const OAuthRedirectPage = ({ platform }: Props) => {
  const [searchParams] = useSearchParams();
  const { createCalendar } = useCreateCalendarMutation();
  const { calendars } = useCalendars();
  const navigate = useNavigate();

  const [createdCalendarId, setCreatedCalendarId] = useState<string | null>(null);

  const code = useMemo(() => searchParams.get('code') ?? '', [searchParams]);
  const state = useMemo(() => searchParams.get('state') ?? '', [searchParams]);
  const error = useMemo(() => searchParams.get('error') ?? '', [searchParams]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (error && !hasRunRef.current) {
      hasRunRef.current = true;
      const title = 'The calendar connection was not successful';
      const message = `There was an error connecting your calendar. The provider returned the error: ${error}. Please try again or contact support if the issue persists.`;
      if (OAUTH_USER_ERRORS.includes(error)) {
        notifyAndWarn(title, message);
      } else {
        notifyAndLogError(title, message, new Error(`OAuth error: ${error}`));
      }

      navigate('/meetings');
      return;
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!code || hasRunRef.current) return;
    hasRunRef.current = true;
    (async () => {
      try {
        const calendar = await createCalendar({ authCode: code, platform, state });
        setCreatedCalendarId(calendar.id);
      } catch (error) {
        notifyAndLogError(
          'Failed to sync calendar',
          'Something went wrong. Please try again.',
          error,
        );
        navigate('/meetings');
      }
    })();
  }, [code, createCalendar, navigate]);

  const createdCalendar = calendars.find(c => c.id === createdCalendarId);
  useEffect(() => {
    if (!createdCalendarId || !createdCalendar) {
      return;
    }

    if (createdCalendar.status === 'DISCONNECTED') {
      notifyAndLogError(
        'Failed to connect calendar',
        'Please try again later.',
        new Error('Calendar connection resulted in DISCONNECTED status'),
        { calendarId: createdCalendarId },
      );
    } else {
      dispatchAppEvent('CALENDAR_CONNECTED');
    }

    navigate('/meetings');
  }, [createdCalendarId, createdCalendar, navigate]);

  return (
    <Center h="100%">
      <PageLoader />
    </Center>
  );
};
